import type { InfiniteData, QueryClient } from "@tanstack/vue-query";

import type { AutomationPage } from "@/lib/api/queries/automations/list";
import type { SubscriberPage } from "@/lib/api/queries/subscribers/list";
import type { SurveyPage } from "@/lib/api/queries/surveys/list";
import type { TagPage } from "@/lib/api/queries/tags/list";
import { filterInfiniteQueries } from "@/lib/api/queries/utils";
import { INTEGRATION_ID_TO_CONFIGURATION } from "@/screens/Settings/integrations";
import { useStore as useEmailsStore } from "@/store/stores/emails";
import type { Permission } from "@/types/account";
import type { Newsletter } from "@/types/newsletter";
import type { SearchResult } from "@/types/search_result";

import { SEARCH_CONTEXT } from "./routes";

function includes(scopes: string[], type: string): boolean {
  return scopes.length === 0 || scopes.includes(type);
}

export function findDataResults({
  q,
  queryClient,
  newsletter,
  scopes = [],
}: {
  q: string;
  queryClient?: QueryClient;
  newsletter: Newsletter;
  scopes?: string[];
}): SearchResult[] {
  const results: SearchResult[] = [];

  const emailsStore = useEmailsStore();
  if (includes(scopes, "email")) {
    for (const email of emailsStore.resource) {
      if (!email.subject.toLowerCase().includes(q)) {
        continue;
      }

      results.push({
        type: "email",
        text: email.subject,
        obj: email,
      });
    }
  }

  // Subscribers
  if (queryClient && includes(scopes, "subscriber")) {
    const queries = queryClient.getQueriesData<InfiniteData<SubscriberPage>>({
      queryKey: [newsletter.id, "subscribers", "list"],
    });

    const found = filterInfiniteQueries(queries, (subscriber) => {
      return subscriber.email_address.toLowerCase().includes(q);
    });

    for (const subscriber of found) {
      results.push({
        type: "subscriber",
        text: subscriber.email_address,
        obj: subscriber,
      });
    }
  }

  // Surveys
  if (queryClient && includes(scopes, "survey")) {
    const queries = queryClient.getQueriesData<InfiniteData<SurveyPage>>({
      queryKey: [newsletter.id, "surveys", "list"],
    });

    const found = filterInfiniteQueries(queries, (survey) => {
      return (
        survey.identifier.toLowerCase().includes(q) ||
        survey.question.toLowerCase().includes(q)
      );
    });

    for (const survey of found) {
      results.push({
        type: "survey",
        text: survey.identifier,
        obj: survey,
      });
    }
  }

  // Tags
  if (queryClient && includes(scopes, "tag")) {
    const queries = queryClient.getQueriesData<InfiniteData<TagPage>>({
      queryKey: [newsletter.id, "tags", "list"],
    });

    const found = filterInfiniteQueries(queries, (tag) => {
      return tag.name.toLowerCase().includes(q);
    });

    for (const tag of found) {
      results.push({
        type: "tag",
        text: tag.name,
        obj: tag,
      });
    }
  }

  // Automations
  if (queryClient && includes(scopes, "automation")) {
    const queries = queryClient.getQueriesData<InfiniteData<AutomationPage>>({
      queryKey: [newsletter.id, "automations", "list"],
    });

    const found = filterInfiniteQueries(queries, (automation) => {
      return automation.name.toLowerCase().includes(q);
    });

    for (const automation of found) {
      results.push({
        type: "automation",
        text: automation.name,
        obj: automation,
      });
    }
  }

  return results;
}

export function findStaticResults({
  q,
  permissions,
  scopes = [],
}: {
  q: string;
  permissions: Permission["permissions"];
  scopes?: string[];
}): SearchResult[] {
  const results: SearchResult[] = [];
  if (q === "") {
    return results;
  }

  if (includes(scopes, "page")) {
    for (const [route, config] of Object.entries(SEARCH_CONTEXT)) {
      if (!config.title.toLowerCase().includes(q)) {
        if (
          !config.matches ||
          !config.matches.some((match) =>
            q.toLowerCase().includes(match.toLowerCase())
          )
        ) {
          continue;
        }
      }

      if (
        config.permission &&
        (permissions[config.permission] === undefined ||
          permissions[config.permission] === "none")
      ) {
        continue;
      }

      results.push({
        type: "page",
        text: config.title,
        feature: config.feature,
        obj: {
          id: config.title,
          name: route as any,
        },
      });
    }
  }

  if (includes(scopes, "page")) {
    for (const [id, config] of Object.entries(
      INTEGRATION_ID_TO_CONFIGURATION
    )) {
      if (!config.name.toLowerCase().includes(q)) {
        continue;
      }

      results.push({
        type: "page",
        text: `Configure ${config.name} integration`,
        brandIcon: config.brandIcon,
        obj: {
          id: `Configure ${config.name} integration`,
          name: "/settings/integrations/[id]",
          params: { id },
        } as any,
      });
    }
  }

  return results;
}
